import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  IconCubes2Outline18,
  IconEyeSlashOutline18,
  IconMergeOutline18,
  IconOpenInNewWindowOutline18,
} from 'nucleo-ui-outline-18'
import { toast } from 'sonner'
import {
  CatalogCompleteness,
  CatalogEnrichmentPill,
  CatalogStatusPill,
  CatalogThumb,
} from '~/components/admin/catalog-bits'
import { openCatalogMergeModal } from '~/components/admin/catalog-merge-modal'
import {
  CatalogAssetsPanel,
  CatalogFieldsPanel,
  CatalogImagesPanel,
  CatalogObservationsPanel,
  CatalogOptionsPanel,
  CatalogUrlsPanel,
} from '~/components/admin/catalog-product-panels'
import { DetailBadge, StatBox } from '~/components/admin/detail-bits'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { BackLink, PageHeader } from '~/components/ui/page-header'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Spinner } from '~/components/ui/spinner'
import {
  CATALOG_STATUS_ORDER,
  catalogCategoryLabel,
  catalogMatchLabel,
  catalogStatusLabel,
  isCatalogStatus,
} from '~/lib/catalog-labels'
import { confirm } from '~/lib/confirm'
import { formatRelativeTime } from '~/lib/datetime'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'
import type { CatalogProductStatus } from '~convex/lib/catalogEnums'

export const Route = createFileRoute('/admin/catalog/$productId')({
  component: AdminCatalogProduct,
  head: () => ({ meta: [{ title: titleFor(msg`Catalog product`) }] }),
})

/**
 * The status control: a `Select` with an `items` map (the trigger renders
 * `SelectValue`). Hiding asks first — it drops the product from every
 * future search while acervo rows keep their snapshot.
 */
const StatusControl = ({
  productId,
  status,
}: {
  productId: Id<'catalogProducts'>
  status: CatalogProductStatus
}) => {
  const { t, i18n } = useLingui()
  const { mutateAsync: setStatus, isPending } = useMutation({
    mutationFn: useConvexMutation(api.catalogAdmin.setStatus),
  })
  const statusItems: Record<string, string> = {}
  for (const entry of CATALOG_STATUS_ORDER) {
    statusItems[entry] = catalogStatusLabel(i18n, entry)
  }
  const change = async (next: string | null) => {
    if (next === null || !isCatalogStatus(next) || next === status) {
      return
    }
    if (next === 'hidden') {
      const confirmed = await confirm({
        confirmLabel: t`Hide product`,
        description: t`Hidden products leave every future search and picker. Library rows that already reference it keep their snapshot.`,
        destructive: true,
        icon: <IconEyeSlashOutline18 />,
        title: t`Hide this product?`,
      })
      if (!confirmed) {
        return
      }
    }
    try {
      await setStatus({ productId, status: next })
      toast.success(t`Status set to ${statusItems[next]}.`)
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : t`Couldn't change the status.`)
    }
  }
  return (
    <Select disabled={isPending} items={statusItems} onValueChange={change} value={status}>
      <SelectTrigger aria-label={t`Status`} className="min-w-40">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {CATALOG_STATUS_ORDER.map((entry) => (
          <SelectItem key={entry} value={entry}>
            {statusItems[entry]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * `/admin/catalog/$productId` — one product, made legible: the projected
 * row with every field's provenance, its options and variants, gallery,
 * files, pages, and the observations it was built from — and the curation
 * actions on it: status, lock a field, make a page canonical, merge.
 */
function AdminCatalogProduct() {
  const { t, i18n } = useLingui()
  const navigate = useNavigate()
  const { productId } = Route.useParams()
  const id: Id<'catalogProducts'> = productId as Id<'catalogProducts'>
  const { data } = useQuery(convexQuery(api.catalogAdminQueries.getProduct, { productId: id }))

  if (data === undefined) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (data === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          description={t`No catalog product has this id.`}
          icon={IconCubes2Outline18}
          title={t`Product not found`}
        />
      </div>
    )
  }

  const {
    libraryCount,
    libraryCountTruncated,
    assets,
    brand,
    collection,
    images,
    product,
    tombstone,
    urls,
  } = data
  const primary = images.find((image) => image.key === product.primaryImageKey) ?? images[0]

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        above={<BackLink to="/admin/catalog">{t`Product catalog`}</BackLink>}
        meta={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {brand === null ? null : (
              <Link
                className="text-primary hover:underline"
                search={{ brandId: brand._id }}
                to="/admin/catalog"
              >
                {brand.name}
              </Link>
            )}
            <span>·</span>
            <span>
              {catalogCategoryLabel(i18n, product.category)}
              {product.subcategory === undefined ? '' : ` · ${product.subcategory}`}
            </span>
            {collection === null ? null : (
              <>
                <span>·</span>
                <Link
                  className="text-primary hover:underline"
                  search={{ collection: collection._id }}
                  to="/admin/catalog"
                >
                  {collection.name}
                  {collection.year === null ? '' : ` ${collection.year}`}
                </Link>
              </>
            )}
            <CatalogStatusPill status={product.status} />
            <CatalogEnrichmentPill enrichment={product.enrichment} />
            <CatalogCompleteness value={product.completeness} />
            <DetailBadge>{catalogMatchLabel(i18n, product.matchSource)}</DetailBadge>
          </span>
        }
        title={
          <span className="flex items-center gap-3">
            <CatalogThumb alt="" className="size-9" url={primary?.thumbUrl ?? null} />
            <span>{product.name}</span>
          </span>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <a
            className="inline-flex items-center gap-1 text-primary text-sm hover:underline"
            href={product.sourceUrl}
            rel="noreferrer"
            target="_blank"
          >
            <Trans>Canonical page</Trans>
            <IconOpenInNewWindowOutline18 className="size-3" />
          </a>
          <StatusControl productId={product._id} status={product.status} />
          <Button
            onClick={() =>
              openCatalogMergeModal({
                loser: {
                  _id: product._id,
                  brandName: brand?.name ?? '—',
                  imageCount: product.imageCount,
                  name: product.name,
                  slug: product.slug,
                  status: product.status,
                  thumbUrl: primary?.thumbUrl ?? null,
                },
                onMerged: (winnerId) =>
                  navigate({ params: { productId: winnerId }, to: '/admin/catalog/$productId' }),
              })
            }
            type="button"
            variant="outline"
          >
            <IconMergeOutline18 />
            <Trans>Merge into…</Trans>
          </Button>
        </div>
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-5xl flex-col gap-8 px-5 py-5">
          {tombstone === null ? null : (
            <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
              <Trans>
                “{tombstone.name}” was merged into this product; you were redirected from its old
                id.
              </Trans>
            </div>
          )}
          {product.matchSource === 'fuzzy' ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
              <span>
                <Trans>
                  This row rests on a fuzzy match. A page was linked using a heuristic the tools
                  weren’t sure about.
                </Trans>
              </span>
              <Link className="font-medium text-primary hover:underline" to="/admin/catalog/review">
                <Trans>Open the review queue</Trans>
              </Link>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatBox label={t`Images`} value={i18n.number(product.imageCount)} />
            <StatBox label={t`Sources`} value={i18n.number(product.sources.length)} />
            <StatBox
              label={t`Used in acervo`}
              value={`${i18n.number(libraryCount)}${libraryCountTruncated ? '+' : ''}`}
            />
            <StatBox label={t`Last seen`} value={formatRelativeTime(product.lastSeenAt, i18n)} />
          </div>

          <CatalogFieldsPanel product={product} />
          <CatalogOptionsPanel images={images} product={product} />
          <CatalogImagesPanel
            images={images}
            primaryKey={product.primaryImageKey}
            sources={product.sources}
          />
          <CatalogAssetsPanel assets={assets} />
          <CatalogUrlsPanel productId={product._id} urls={urls} />
          <CatalogObservationsPanel productId={product._id} />
        </div>
      </div>
    </div>
  )
}
